"use client";

import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AeraButton } from "./AeraButton";
import {
  useHorizontalScrollRail,
  type HorizontalScrollHints,
  type UseHorizontalScrollRailOptions,
} from "./useHorizontalScrollRail";

function joinClassNames(
  ...parts: Array<string | false | null | undefined>
): string {
  return parts.filter(Boolean).join(" ");
}

const DEFAULT_RAIL_CLASSNAME =
  "flex flex-col gap-3 md:flex-row md:flex-nowrap md:overflow-x-auto md:overscroll-x-contain md:py-3 scrollbar-hide";

export type ScrollRailNavButtonsProps = {
  scrollHints: HorizontalScrollHints;
  onScrollLeft: () => void;
  onScrollRight: () => void;
  leftAriaLabel?: string;
  rightAriaLabel?: string;
  hideOnMobile?: boolean;
  iconClassName?: string;
  iconStrokeWidth?: number;
  leftButtonClassName?: string;
  rightButtonClassName?: string;
  /** Tailwind `from-* via-*` pair for the edge fade (default white). */
  edgeFadeFromClassName?: string;
};

export function ScrollRailNavButtons({
  scrollHints,
  onScrollLeft,
  onScrollRight,
  leftAriaLabel = "เลื่อนซ้าย",
  rightAriaLabel = "เลื่อนขวา",
  hideOnMobile = true,
  iconClassName = "h-6 w-6 text-aera-gray",
  iconStrokeWidth = 2,
  leftButtonClassName,
  rightButtonClassName,
  edgeFadeFromClassName = "from-aera-white via-aera-white/80",
}: ScrollRailNavButtonsProps) {
  const mobileVisibility = hideOnMobile ? "hidden md:flex" : "flex";

  return (
    <>
      {scrollHints.left ? (
        <AeraButton
          type="button"
          variant="transparent"
          size="xs"
          paddingSize="none"
          aria-label={leftAriaLabel}
          onClick={onScrollLeft}
          className={joinClassNames(
            "absolute top-0 bottom-0 left-0 z-10 h-auto w-9 justify-start! rounded-none hover:bg-transparent bg-linear-to-r to-transparent pl-0.5",
            edgeFadeFromClassName,
            mobileVisibility,
            leftButtonClassName,
          )}
          icon={
            <ChevronLeft className={iconClassName} strokeWidth={iconStrokeWidth} />
          }
        />
      ) : null}
      
      {scrollHints.right ? (
        <AeraButton
          type="button"
          variant="transparent"
          size="xs"
          paddingSize="none"
          aria-label={rightAriaLabel}
          onClick={onScrollRight}
          className={joinClassNames(
            "absolute top-0 right-0 bottom-0 z-10 h-auto w-9 justify-end! rounded-none hover:bg-transparent bg-linear-to-l to-transparent pr-0.5",
            edgeFadeFromClassName,
            mobileVisibility,
            rightButtonClassName,
          )}
          icon={
            <ChevronRight
              className={iconClassName}
              strokeWidth={iconStrokeWidth}
            />
          }
        />
      ) : null}
    </>
  );
}

export type CardScrollRailProps = UseHorizontalScrollRailOptions & {
  children: ReactNode;
  className?: string;
  railClassName?: string;
  leftAriaLabel?: string;
  rightAriaLabel?: string;
  hideButtonsOnMobile?: boolean;
  iconClassName?: string;
  iconStrokeWidth?: number;
  leftButtonClassName?: string;
  rightButtonClassName?: string;
  /** Tailwind `from-* via-*` pair for the edge fade (default white). */
  edgeFadeFromClassName?: string;
};

export function CardScrollRail({
  children,
  className,
  railClassName,
  syncKey,
  scrollStep,
  itemSelector,
  defaultItemWidth,
  railRef: externalRailRef,
  outerRef,
  pinRailWidthToOuter,
  leftAriaLabel,
  rightAriaLabel,
  hideButtonsOnMobile = true,
  iconClassName,
  iconStrokeWidth,
  leftButtonClassName,
  rightButtonClassName,
  edgeFadeFromClassName,
}: CardScrollRailProps) {
  const { railRef, scrollHints, scrollByDirection } = useHorizontalScrollRail({
    syncKey,
    scrollStep,
    itemSelector,
    defaultItemWidth,
    railRef: externalRailRef,
    outerRef,
    pinRailWidthToOuter,
  });

  return (
    <div className={joinClassNames("relative min-w-0", className)}>
      <ScrollRailNavButtons
        scrollHints={scrollHints}
        onScrollLeft={() => scrollByDirection("left")}
        onScrollRight={() => scrollByDirection("right")}
        leftAriaLabel={leftAriaLabel}
        rightAriaLabel={rightAriaLabel}
        hideOnMobile={hideButtonsOnMobile}
        iconClassName={iconClassName}
        iconStrokeWidth={iconStrokeWidth}
        leftButtonClassName={leftButtonClassName}
        rightButtonClassName={rightButtonClassName}
        edgeFadeFromClassName={edgeFadeFromClassName}
      />
      <div
        ref={railRef}
        className={railClassName ?? DEFAULT_RAIL_CLASSNAME}
      >
        {children}
      </div>
    </div>
  );
}
